import axios from "axios";
import { getNavigate } from "../navigation";

/**
 * Axios instance configured with base URL from environment variable (.env)
 */
const apiBaseUrl = process.env.REACT_APP_API_URL;
if (!apiBaseUrl) {
  throw new Error("API base URL missing");
}

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 25000, // 25-second request timeout
});


/* Add Authorization header if token exists */
api.interceptors.request.use((config) => {
  const token = JSON.parse(localStorage.getItem("token")); //future upgarde change local storage to HTTP Only cookie
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration / 401 globally
let isLoggingOut = false;
/* Response Interceptor */
api.interceptors.response.use(
  (response) => {
    return {
      success: true,
      message: response.data?.message || "Request successful",
      data: response.data?.data ?? response.data, // fallback if API doesn't wrap in `data`
      status: response.status,
    };
  },
  (error) => {
    const requestUrl = error.config?.url;
    //Handle 401 globally
    if (error.response?.status === 401) {
      // Skip handling for login and register endpoints
      if (requestUrl?.includes("/auth/login")) {
        return Promise.reject({
          success: false,
          message:
            error.response.data?.message ||
            error.response.statusText ||
            "Something went wrong",
          status: error.response.status,
        });
      }
      if (!isLoggingOut) {
        isLoggingOut = true; // prevent multiple alerts
        if (process.env.NODE_ENV !== 'production') {
          console.log("Token expired or invalid. Logging out…");
        }

        //alert("Session expired or Invalid token. Logging out…");
      }
      const navigate = getNavigate();
      if (navigate) {
        navigate("/");
        isLoggingOut = false;
      } else {
        // fallback in case navigate not ready
        window.location.href = "/";
      }

      localStorage.removeItem("token");
      localStorage.removeItem("clientId");
    }

    // Case: Server responded with error
    if (error.response) {
      return Promise.reject({
        success: false,
        message:
          error.response.data?.message ||
          error.response.statusText ||
          "Something went wrong",
        status: error.response.status,
      });
    }

    // Case: No response (network error, server down, DNS, CORS, etc.)
    return Promise.reject({
      success: false,
      message: error.message || "Network error, please try again later",
      status: 0,
    });
  }
);

// ======================= API ENDPOINTS =====================

// =================== Signup ===================
/**
 * Register a new user
 * @param {object} user
 */
export const signUpUser = (user) => api.post("/auth/register", user);

/**
 * Verify email with code after signup
 * @param {object} verifyCode
 */
export const verifySignUpUserEmail = (verifyCode) => api.post("/auth/verify-email", verifyCode);

// =================== Login ===================

/**
 * Login with email and password
 * @param {object} user
 */
export const authLoginUser = (user) => api.post("/auth/login", user);

// =================== User profile ===================

/**
 * Get profile of the logged-in user
 */
export const getUserProfile = () => api.get("/auth/profile");

// =================== Password reset ===================

/**
 * Send forgot-password request (email)
 * @param {object} email
 */
export const verifyForgotPasswordEmail = (email) => api.post("/auth/forgot-password", email);

/**
 * Reset password with confirmation code
 * @param {object} user
 */
export const resetPassword = (user) => api.post("/auth/reset-password", user);

// =================== Leads ===================

/**
 * Get paginated leads
 * @param {string} clientId
 * @param {number} page
 * @param {number} limit
 */
export const getLeads = (clientId, page, limit) =>
  api.get("/leads", { params: { clientId, page, limit } });

/**
 * Get paginated leads
 * @param {string} campaignId
 * @param {string} clientId
 * @param {number} page
 * @param {number} limit
 */
export const getLeadsByCampaignId = (campaignId, clientId, page, limit) =>
  api.get(`/leads/campaign/${campaignId}`, { params: { clientId, page, limit } });

/**
 * Delete a lead
 * @param {string} id
 */
export const deleteLead = (id) => api.delete(`/leads/${id}`);

/**
 * Update a lead
 * @param {string} id
 * @param {object} lead
 */
export const updateLead = (id, lead) => api.put(`/leads/${id}`, lead);

/**
 * Add a new lead
 * @param {object} lead
 */
export const addLead = (lead) => api.post("/leads", lead);

/**
 * AI Lead Generation
 * @param {object} payload
 */
export const genLead = (payload) => api.post("/apollo/scrape", payload);

// =================== Business Info ===================

/**
 * Add new business details
 * @param {object} payload
 * @param {string} clientId
 */
export const addBusiness = (payload) =>
  api.post(`/clients/business-details`, payload);

/**
 * Get business details for a client
 * @param {string} clientId
 */
export const getBusinessDetails = (clientId) =>
  api.get(`/clients/${clientId}/business-details`);

/**
 * Update business details for a client
 * @param {string} id
 * @param {object} payload
 */
export const updateBusiness = (id, payload) =>
  api.put(`/clients/${id}/business-details`, payload);

// =================== Campaigns ===================

/**
 * Add a new campaign
 * @param {object} campaign
 */
export const addCampaign = (campaign) => api.post("/campaigns", campaign);

/**
 * Get paginated campaigns
 * @param {string} clientId
 * @param {number} page
 * @param {number} limit
 * @param {string} search
 */
export const getCampaigns = (clientId, page, limit, search) =>
  api.get("/campaigns", { params: { clientId, page, limit, search } });

/**
 * Update a Campaign
 * @param {string} id
 * @param {object} campaign
 */
export const updateCampaign = (id, campaign) => api.patch(`/campaigns/${id}`, campaign);

/**
 * Update a Campaign with followups
 * @param {string} id
 * @param {object} campaign
 */
export const updateCampaignFollowups = (id, campaign) => api.patch(`/campaigns/${id}`, campaign);

/**
 * Delete a campaign
 * @param {string} id
 */
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);

// =================== Dashboard and analytics ===================

/**
 * Get Dashboard data
 * @param {string} clientId
 */
export const getDashboard = (clientId) =>
  api.get("/dashboard", { params: { clientId } });

/**
 * Get Campaign Analytics
 * @param {string} clientId
 */
export const getCampaignAnalytics = (clientId) =>
  api.get("/analytics/campaigns", { params: { clientId } });

/**
 * Get Lead Analytics
 * @param {string} clientId
 */
export const getLeadsAnalytics = (clientId) =>
  api.get("/analytics/leads", { params: { clientId } });

/**
 * Get Email Analytics
 * @param {string} clientId
 */
export const getEmailAnalytics = (clientId) =>
  api.get("/analytics/emails", { params: { clientId } });

/**
 * Get Email Logs by Campaign
 * @param {string} campaignId
 */
export const getEmailsByCampaign = (campaignId) =>
  api.get(`/emails/campaign/${campaignId}`);

// =================== DNS verification for sendgrid ===================

/**
 * Add DNS records for a client (SendGrid domain authentication)
 * @param {string} clientId
 * @param {string} domain
 */
export const addClientDns = (clientId, domain) =>
  api.post(`/clients/${clientId}/dns`, { domain });

/**
 * Verify DNS for a client
 * @param {string} clientId
 */
export const verifyClientDns = (clientId) =>
  api.post(`/clients/${clientId}/dns/verify`);

// =================== Business Info generation from ollamma ===================

/**
 * Stream "About your business" generation from LLM
 * @param {string} prompt
 * @param {(chunk: string) => void} onChunk Callback for each data chunk
 */
export const streamBusinessInfo = async (prompt, onChunk) => {
  const token = JSON.parse(localStorage.getItem("token"));
  if (!token) throw new Error("No auth token found");

  const res = await fetch(`${process.env.REACT_APP_API_URL}/llm/prompt/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to start stream");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");

  let done = false;
  while (!done) {
    const { value, done: streamDone } = await reader.read();
    done = streamDone;
    if (value) {
      const chunk = decoder.decode(value, { stream: true });
      // Send chunk to callback
      onChunk(chunk);
    }
  }
};

/**
 * Generate business details (non-streaming)
 * @param {string} prompt
 */
export const generateFromPrompt = (prompt) =>
  api.post("/llm/prompt/full", { prompt });

// =================== Campaign Attachments ===================

/**
 * Upload attachments for a campaign
 * @param {string} campaignId
 * @param {Array} files Array of { name, url, size, type, key }
 */
export const uploadCampaignAttachments = (campaignId, files) =>
  api.patch(`/campaigns/${campaignId}`, { attachments: files });

/**
 * Delete a single attachment from a campaign
 * @param {string} campaignId
 * @param {Array} files updated file 
 */
export const deleteCampaignAttachment = (campaignId, files) =>
  api.patch(`/campaigns/${campaignId}`, { attachments: files });

/**
 * Get files for a client and campaign
 * @param {string} clientId 
 * @param {string} campaignId 
 */
export const getCampaignFiles = (clientId, campaignId) =>
  api.get(`/storage/${clientId}/${campaignId}`);

/**
 * Generate signed URL for file upload
 * @param {Object} file - { name, type, size }
 * @param {string} clientId 
 * @param {string} campaignId 
 */
export const getSignedUrlForFile = (file, clientId, campaignId) =>
  api.post('/storage/signed-url', {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    userId: clientId,
    campaignId: campaignId,
  });

/**
 * Delete a file from storage
 * @param {string} key - File key to delete
 */
export const deleteStorageFile = (key) => {
  return api.delete(`/storage/${encodeURIComponent(key)}`);
};

// =============== ADMIN ==================

/**
 * Get admin dashboard metrics
 * 
 */
export const getAdminDashboard = () =>
  api.get("/admin/dashboard");

/**
 * Get paginated all users including admin users
 * 
 * @param {number} page
 * @param {number} limit
 */
export const getAllUsers = (page, limit) =>
  api.get("/admin/users", { params: { page, limit} });

/**
 * Get paginated client users with their stats
 * 
 * @param {number} page
 * @param {number} limit
 */
export const getClientUsers = (page, limit) =>
  api.get("/admin/clients", { params: { page, limit } });

/**
 * Manage users - with action: 'activate' | 'deactivate' | 'suspend' | 'delete';
 * payload - 
 { 
   userId (clientId),
   action ('activate' | 'deactivate' | 'suspend' | 'delete'), 
   reason (optional) 
 }
 * @param {object} payload
 */
export const manageUser = (payload) =>
  api.post("/admin/users/manage", payload)

// ============= Subscription ================

/**
 * Get all subscription plans details
 */
export const getSubscription = () =>
  api.get("/subscription/plans");

/**
 * Subscription status
 * @param {string} clientId 
 */
export const getSubscriptionStatus = (clientId) =>
  api.get("/subscription/status", clientId)

/**
 * Update Subscription plan
 * @param {string} userId
 * @param {object} payload
 */
export const updateUserSubscriptionPlan = (userId, payload) =>
  api.put(`/subscription/`, { params: { userId } }, payload);

/**
 * Get User Subscription plan
 * @param {string} userId
 */
export const getUserSubscriptionPlan = (userId) =>
  api.get(`/subscription/user/${userId}`);

// ============= Subscription Plans ================

/**
 * Get subscription details
 */
export const getSubscriptionPlans = (includeInactive = true) =>
  api.get('/subscription-plans', { params: { includeInactive } });

/**
 * Create subscription
 * @param {object} payload
 */
export const createSubscriptionPlan = (payload) =>
  api.post('/subscription-plans', payload)

/**
 * Update subscription
 * @param {string} id
 * @param {object} payload
 * 
 */
export const updateSubscriptionPlan = (id, payload) =>
  api.put(`/subscription-plans/${id}`, payload)

/**
 * delete subscription
 * @param {string} id
 */
export const deleteSubscriptionPlan = (id) =>
  api.delete(`/subscription-plans/${id}`)

// =============== Workflow mapping service =================

/**
 * Get paginated workflow mappings
 * @param {number} page
 * @param {number} limit
 * @param {string} search
 */
export const getWorkflowMapping = (page, limit, search) =>
  api.get("/workflow-mappings", { params: { page, limit, search } });

/**
 * 
 * Get workflow mapping summary
 */

export const getWorkflowMappingSummary = () =>
  api.get("/workflow-mappings/summary");